import { useState } from 'react';
import '../../styles/onboardingFlow/resetPassword/resetPassword.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useTitle from '../../customHooks/useTitle';
import AINormalInput from '../../ui/inputs/AINormalInput';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { PASSWORD_CHANGE_ENDPOINT } from '../../lib/constants/central-api';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { asyncHandler } from '../../lib/api/async-handler';
import { AnimatePresence } from 'framer-motion';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import PasswordValidationView from './PasswordValidationView';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import OnboardingMobileLogoPanel from '../onboardingComponents/OnboardingMobileLogoPanel';
import OnboardingBackButton from '../onboardingComponents/OnboardingBackButton';
import store from '../../store/store';
import {
  setAgencyAccessToken,
  setEmployeeIdentity,
} from '../../store/reducers/authSlice';
import {
  employeeResetPassword,
  fetchIdentity,
} from '../../lib/api/identity/identity-api';
import { HOME_ROUTE } from '../../lib/constants/route';

interface ResetPasswordState {
  token: string;
}

const ResetPassword = () => {
  const { t } = useTranslation();
  useTitle(t('ResetPasswordMainText'));
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ResetPasswordState | undefined;

  // An agency EMPLOYEE arrives here from an emailed link carrying
  // `?employee_reset=<token>` — there is no in-app OTP step and no
  // `location.state`, and `state.user.profile.email` is empty because they are
  // not signed in. That flow posts the token to the employee endpoint instead
  // of the seller password-change endpoint, and signs them straight in.
  const employeeResetToken = new URLSearchParams(location.search).get(
    'employee_reset'
  );
  const isEmployeeReset = isNotEmptyString(employeeResetToken ?? '');

  const token = isEmployeeReset ? employeeResetToken ?? '' : state?.token;

  const userEmail = useSelector((state: RootState) => state.user.profile.email);
  const [resetError, setResetError] = useState<string | null>(null);

  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);

  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [isLongEnough, setIsLongEnough] = useState<boolean>(false);

  const validatePassword = (password: string) => {
    setHasUppercase(/[A-Z]/.test(password));
    setHasLowercase(/[a-z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[^a-zA-Z0-9]/.test(password));
    setIsLongEnough(password.length >= 8);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const showPasswordValidationView =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough;

  const isDisabled =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough ||
    password !== confirmPassword ||
    !isNotEmptyString(token) ||
    // The employee link carries its own identity in the token; there is no
    // signed-in profile to read an email from.
    (!isEmployeeReset && !isNotEmptyString(userEmail));

  async function onSubmit(e: any) {
    e.preventDefault();
    if (!token || token === '') return;

    if (isEmployeeReset) {
      await onEmployeeSubmit(token);
      return;
    }

    const sendingData = {
      email: userEmail,
      password,
      password_confirmation: confirmPassword,
      token: token,
    };
    try {
      const requestInfo: RequestInfo = {
        url: PASSWORD_CHANGE_ENDPOINT,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'PASSWORD_CHANGE';
      const customMsg = 'Password Changed Successfully!';

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;
      if (res.actionType === actionType && response.success === true) {
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (exception) {
      console.log('Error during authentication request');
    }
  }

  /**
   * Redeem an agency-employee reset token.
   *
   * The token is one-shot and the response carries a freshly minted `emp_` key
   * plus the httpOnly session cookie, so the employee is signed in immediately
   * rather than bounced back to a login they have no seller account for.
   */
  async function onEmployeeSubmit(resetToken: string) {
    setResetError(null);
    try {
      const res = await employeeResetPassword(resetToken, password);

      if (!isNotEmptyString(res?.agency_api_key)) {
        setResetError(t('SomethingWentWrong'));
        return;
      }

      store.dispatch(setAgencyAccessToken(res.agency_api_key));

      try {
        store.dispatch(
          setEmployeeIdentity(await fetchIdentity(res.agency_api_key))
        );
      } catch {
        // Non-fatal: App.tsx retries /me off the agency token.
      }

      navigate(HOME_ROUTE, { replace: true });
    } catch (err: any) {
      setResetError(
        err?.status === 404
          ? 'This reset link has expired or has already been used. Please request a new one.'
          : err?.message || t('SomethingWentWrong')
      );
    }
  }

  return (
    <div className='onboarding-reset-password'>
      {/* Logo panel (mobile only) */}
      <OnboardingMobileLogoPanel />

      <div className='onboarding-reset-password__container'>
        {/* Back button (mobile only) */}
        <OnboardingBackButton
          onClick={() => navigate(-1)}
          aria-label={t('GoBack')}
        />

        {/* Logo (desktop only; hidden on mobile when logo panel is shown) */}
        <div className='onboarding-reset-password__logo'>
          <img src={adupLogoSet} alt='AdUp Logo' />
        </div>

        {/* Heading */}
        <div className='onboarding-reset-password__header'>
          <h1>{t('ResetPasswordMainText')}</h1>
          <p>{t('ResetPasswordSubText-new-ui')}</p>
        </div>

        {/* Inline error for an expired / already-used employee reset link. */}
        {resetError && (
          <p
            style={{
              color: '#b42318',
              fontSize: '0.85rem',
              margin: '0 0 1rem',
              textAlign: 'center',
            }}
          >
            {resetError}
          </p>
        )}

        {/* Form */}
        <form onSubmit={onSubmit} className='onboarding-reset-password__form'>
          {/* Password Input */}
          <div className='onboarding-reset-password__field onboarding-reset-password__field--password'>
            <AINormalInput
              id='reset-password-form-password'
              value={password}
              changeListeners={[(e) => handleChange(e)]}
              placeholder={t('Password')}
              type='password'
              innerPlaceholder={t('EnterYourNewPassword')}
              required={true}
              focusListeners={[
                (e) => {
                  setIsPasswordInputFocused(true);
                },
              ]}
              blurListeners={[
                (e) => {
                  setIsPasswordInputFocused(false);
                },
              ]}
            />
            {/* Password Validation - Floating */}
            <AnimatePresence exitBeforeEnter>
              {isPasswordInputFocused && showPasswordValidationView && (
                <PasswordValidationView
                  hasUppercase={hasUppercase}
                  hasLowercase={hasLowercase}
                  hasNumber={hasNumber}
                  hasSpecialChar={hasSpecialChar}
                  isLongEnough={isLongEnough}
                />
              )}
            </AnimatePresence>
          </div>

          {/* Confirm Password Input */}
          <div className='onboarding-reset-password__field'>
            <AINormalInput
              id='reset-password-form-confirm-password'
              value={confirmPassword}
              type='password'
              changeListeners={[(e) => setConfirmPassword(e.target.value)]}
              placeholder={t('ConfirmPassword')}
              innerPlaceholder={t('ConfirmYourNewPassword')}
              required={true}
            />
          </div>

          {/* Reset Password Button */}
          <div
            className='onboarding-reset-password__button'
            title={isDisabled ? t('ConfirmPasswordDoNotMatch') : ''}
          >
            <MainSquareButton
              value={t('ChangePassword')}
              type='submit'
              disabled={isDisabled}
              classes='onboarding-reset-password__continue-btn'
            />
          </div>
        </form>
      </div>

      {/* Copyright */}
      <div className='onboarding-reset-password__copyright'>
        <span>© AdUp {new Date().getFullYear()}</span>
      </div>
    </div>
  );
};

export default ResetPassword;
